import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Container, Paper, Typography, Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import Loading from '../../components/common/Loading.component';
import { useNavigate, useParams } from 'react-router-dom';
import ListView from '../../views/List.view';
import ToolbarComponent from '../../components/toolbar/Toolbar.component';
import _ from 'lodash';

const TemplateList = ({
  module: { tag, query, filter, list, search, action },
  children,
  extraActions,
  onItemClick,
}) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { jwt } = useSelector((state) => state.login);
  const {
    data: { loading, data },
    list: {
      selected,
      pagination: { page },
    },
  } = useSelector((state) => state[tag]);

  const { from } = useSelector((state) => state.year);
  const _query = useSelector(query.all.selector);
  const firstRenderRef = useRef(true);

  const userAuth = useSelector((state) => state.login?.user);
  const hasPermissions = useMemo(
    () => userAuth.permissions[_.toUpper(tag)]?.READ === 'true',
    [userAuth, tag]
  );

  // Default action on click
  const onClick = useCallback(
    (item) => {
      const yachtName =
        item?.attributes?.id_yacht?.data?.attributes?.yacht_name;
      const orderType = item?.attributes?.id_service?.data?.attributes?.service;
      navigate(`${item.id}`, {
        state: { from: tag, yachtName: yachtName, orderType: orderType },
      });
    },
    [navigate, tag]
  );

  const handleItemClick = useCallback((item) => {
    if (onItemClick) {
      onItemClick(item);
      return;
    }
    onClick(item);
  }, [onItemClick, onClick]);

  const fetchData = useCallback(() => {
    const params = id ? { id } : {};
    dispatch(
      query.all.action({
        settings: query.all.settings,
        token: jwt,
        params,
        query: { ..._query, populate: query.all.populate },
      })
    );
  }, [_query, dispatch, jwt, query, id]);

  useEffect(() => {
    if (_.isEmpty(selected) && hasPermissions) {
      fetchData();
    }
    // eslint-disable-next-line
  }, [_query, dispatch, jwt, query, id, hasPermissions, from, selected]);

  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    if (hasPermissions) {
      fetchData();
    }
    // eslint-disable-next-line
  }, [page, firstRenderRef]);

  const onSelectItem = useCallback(
    (row) => {
      dispatch(list.actions.selected({ id: row.id, page }));
    },
    [dispatch, list, page]
  );

  const onSelectAll = useCallback(
    (isChecked) => {
      if (isChecked) {
        dispatch(list.actions.selectedAll({ [page]: [] }));
      } else {
        const all = _.map(data, (item) => item?.id);
        dispatch(list.actions.selectedAll({ [page]: all }));
      }
    },
    [dispatch, list, data, page]
  );

  useEffect(() => {
    if (list.actions.resetSelect) dispatch(list.actions.resetSelect());
  }, [from, list, dispatch]);

  return (
    <Container disableGutters={true} maxWidth={false} sx={{ height: '100%' }}>
      {!list?.noTag && (
        <Box display={'flex'} justifyContent='space-between' padding={3}>
          <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
            {_.upperFirst(tag)}
          </Typography>
        </Box>
      )}
      <Paper variant='outlined'>
        <ToolbarComponent
          tag={tag}
          search={search}
          filter={filter}
          add={list?.add}
          link={list?.link}
          list={list}
          action={action}
          query={query}
        />

        {extraActions && (
          <Box display='flex' justifyContent='flex-end' px={2} py={2}>
            {extraActions}
          </Box>
        )}
        {loading && <Loading />}
        {hasPermissions && (
          <ListView
            list={list}
            onClick={handleItemClick}
            onSelectItem={onSelectItem}
            selected={selected}
            onSelectAll={onSelectAll}
          >
            {children}
          </ListView>
        )}
      </Paper>
    </Container>
  );
};

export default TemplateList;
